import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogActions, Button } from '@airbus/components-react';
import { styled } from '@mui/material/styles';
import publicVersionObject from '../../../public/version.json';
import styles from './ReleaseNotes.module.css';
import { useFeatureFlagEnabled, useFeatureFlagPayload } from 'posthog-js/react';

type ReleaseNoteType = {
  version: string;
  dateString: string;
  summary: string;
  whatsNew?: string[];
  enhancements?: string[];
  bugFixes?: string[];
};

const CustomDialog = styled(Dialog)`
  max-width: 60%;
  max-height: 80%;

  @media (max-width: 600px) {
    max-width: 80%;
  }
`;

export function getVersionNumber(version: string | null): number {
  let versionNumber: number | null = null;
  if (version) {
    const match = version.match(/r(\d{8})/);
    if (match && match[1]) {
      versionNumber = Number(match[1]);
    }
  }
  return versionNumber;
}

function isArrayOfStrings(value: unknown): boolean {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function isValidReleasePayload(payload: ReleaseNoteType | undefined | null): boolean {
  if (!payload) return false;
  const { summary, whatsNew, enhancements, bugFixes } = payload;
  const hasRequiredText = typeof summary === 'string' && summary.trim().length > 0;
  const hasAnyList =
    isArrayOfStrings(whatsNew) || isArrayOfStrings(enhancements) || isArrayOfStrings(bugFixes);
  return hasRequiredText && hasAnyList;
}

export function compareVersionNumber(
  publicVersion: string,
  publicVersionNumber: number,
  localVersionNumber: number | null,
  setOpen: (open: boolean) => void,
) {
  if (publicVersion !== 'dev') {
    if (publicVersionNumber > localVersionNumber) {
      setOpen(true);
    }
  }
}

export default function ReleaseNotesComponent() {
  const releaseEnabled = useFeatureFlagEnabled('releaseNotes');
  const releasePayload = useFeatureFlagPayload('releaseNotes') as ReleaseNoteType;
  const [open, setOpen] = useState(false);
  const publicVersion = publicVersionObject['app-version'];
  const localVersion = localStorage.getItem('releaseVersion');
  const localVersionNumber = getVersionNumber(localVersion);
  const publicVersionNumber = getVersionNumber(publicVersion);
  useEffect(() => {
    if (releaseEnabled && isValidReleasePayload(releasePayload)) {
      compareVersionNumber(publicVersion, publicVersionNumber, localVersionNumber, setOpen);
    } else {
      setOpen(false);
    }
  }, [releasePayload, releaseEnabled]);

  function handleClose() {
    localStorage.setItem('releaseVersion', publicVersion);
    setOpen(false);
  }

  return (
    releaseEnabled &&
    open && (
      <div>
        <CustomDialog
          fullScreen
          open={open}
          onClose={handleClose}
          disableBackdropClick
          disableEscapeKeyDown
          disablePortal
        >
          <DialogContent>
            <ReleaseNotesDocument releaseNotes={releasePayload} />
          </DialogContent>
          <DialogActions>
            <Button variant='primary' onClick={handleClose}>
              Close
            </Button>
          </DialogActions>
        </CustomDialog>
      </div>
    )
  );
}

function ReleaseNotesDocument({ releaseNotes }: { releaseNotes: ReleaseNoteType }) {
  const { summary, whatsNew, enhancements, bugFixes, version, dateString } = releaseNotes;

  return (
    <div className={styles.container}>
      <div className={styles.h1}>RELEASE NOTES</div>
      <div className={styles.h2}>NPI Tool {version ? version : null}</div>
      <div className={styles.date}>{dateString ? dateString : null}</div>
      <br />
      <div className={styles.section}>
        <div className={styles.h3}>📣 Summary</div>
        <div className={styles.li}>{summary}</div>
      </div>
      {whatsNew && (
        <div className={styles.section}>
          <div className={styles.h3}>🚀 What&apos;s New</div>
          <ul className={styles.ul}>
            {whatsNew?.map((item, idx) => (
              <li className={styles.li} key={idx}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
      {enhancements && (
        <div className={styles.section}>
          <div className={styles.h3}>⭐ Enhancements</div>
          <ul className={styles.ul}>
            {enhancements?.map((item, idx) => (
              <li className={styles.li} key={idx}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
      {bugFixes && (
        <div className={styles.section}>
          <div className={styles.h3}>🐛 Bug Fixes</div>
          <ul className={styles.ul}>
            {bugFixes?.map((item, idx) => (
              <li className={styles.li} key={idx}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
